import React from "react";
import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  Stack,
  TextField,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAddClient } from "../../store";
import { Client } from "../../types";
import toast from "react-hot-toast";

type AddClientModalProps = {
  open: boolean;
  onClose: () => void;
};

const AddClientModal: React.FC<AddClientModalProps> = ({ open, onClose }) => {
  const onCreateSuccess = (client: Client | null) => {
    if (!client) {
      toast.error("Failed to create client");
      return;
    }

    toast.success(`Client ${client.clientName} created successfully`);
  };

  const onCreateFailure = (error: any) => {
    toast.error(`Failed to create client: ${error.message}`);
  };

  const { mutate: addClient } = useAddClient(onCreateSuccess, onCreateFailure);

  const closeForm = () => {
    formik.resetForm();
    onClose();
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ownerEmail: "",
      clientName: "",
    },
    validationSchema: Yup.object({
      ownerEmail: Yup.string()
        .trim()
        .email("Owner Email must be a valid email")
        .required("Owner Email is required"),
      clientName: Yup.string()
        .trim()
        .min(2, "Client Name must be at least 2 characters")
        .required("Client Name is required"),
    }),
    onSubmit: (values) => {
      const { ownerEmail, clientName } = values;

      addClient({
        clientName,
        email: ownerEmail,
      });

      closeForm();
    },
  });

  return (
    <>
      <Dialog open={open} onClose={closeForm} maxWidth={"sm"} fullWidth>
        <DialogTitle component={"div"}>
          <Typography variant="h3">Add Client</Typography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                id="ownerEmail"
                name="ownerEmail"
                label="Owner's Email"
                variant="outlined"
                value={formik.values.ownerEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.ownerEmail && Boolean(formik.errors.ownerEmail)
                }
                helperText={
                  formik.touched.ownerEmail && formik.errors.ownerEmail
                }
              />
              <TextField
                fullWidth
                id="clientName"
                name="clientName"
                label="Client Name"
                variant="outlined"
                value={formik.values.clientName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.clientName && Boolean(formik.errors.clientName)
                }
                helperText={
                  formik.touched.clientName && formik.errors.clientName
                }
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button fullWidth variant="outlined" onClick={closeForm}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={!formik.isValid || !formik.dirty}
              >
                Submit
              </Button>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddClientModal;